/**
 * GatedPatternsSection - Tier-gated wrapper for PatternsSection
 *
 * Applies blur teaser for L1 (free/anonymous) users:
 * - First pattern fully visible, remaining blurred with count overlay
 * - CTA: "Unlock N More Patterns with Drama+"
 * - L2+ users see all patterns with no gating
 *
 * Follows GatedInlineAnnotations pattern.
 *
 * Task: #1158.P1.1
 * Agent: Diego <diego@liftping.net>
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { View } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { useSDUIConfig, toUISpecTier } from '@dramadetector/ui/json-render';
import { GatedSection } from '@/components/gating';
import { useEntitlements } from '@/hooks/useEntitlements';
import { captureEvent } from '@/lib/analytics';
import { PatternsSection } from './PatternsSection';
import type { Pattern } from '@dramadetector/shared';

/** Fallback number of patterns visible to free users before blur (SDUI overrides via patternCountThreshold) */
const DEFAULT_PATTERN_LIMIT = 1;

export interface GatedPatternsSectionProps {
  /** Patterns from analysis response */
  patterns: Pattern[];
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Override tier for testing */
  overrideTier?: 'anonymous' | 'free' | 'drama_plus' | 'dramax' | 'family_member';
  /** Screen name for analytics */
  screen?: string;
  /** Callback when upgrade pressed */
  onUpgrade?: () => void;
  /** Test ID for testing */
  testID?: string;
}

/**
 * GatedPatternsSection - Tier-aware patterns display
 *
 * Tier behavior:
 * - L1 (Free/Anonymous): First pattern visible, rest blurred with CTA
 * - L2 (Drama+): All patterns visible
 * - L3 (DramaX): All patterns visible
 */
export function GatedPatternsSection({
  patterns,
  t,
  overrideTier,
  screen = 'results_full',
  onUpgrade,
  testID = 'gated-patterns-section',
}: GatedPatternsSectionProps) {
  const { tier: detectedTier } = useEntitlements();
  const tier = overrideTier || detectedTier || 'free';

  // Defensive null safety
  const safePatterns = patterns || [];

  // Determine premium access (family members inherit drama_plus/dramax tier from admin).
  // `family_member` is a client-side normalized tier (see `hooks/quota/quotaUtils.ts`) that
  // some callers pass via `overrideTier` — treat it as premium to avoid rendering the blur
  // gate for users who already paid.
  const isPremium = tier === 'drama_plus' || tier === 'dramax' || tier === 'family_member';

  // SDUI config for blur teaser copy interpolation + pattern threshold
  const { config: sduiConfig } = useSDUIConfig('blur-teaser', toUISpecTier(tier));
  const patternLimit = sduiConfig.patternCountThreshold ?? DEFAULT_PATTERN_LIMIT;

  // For free users, only show first N patterns (SDUI-configurable)
  const visiblePatterns = isPremium ? safePatterns : safePatterns.slice(0, patternLimit);
  const gatedPatternCount = isPremium ? 0 : Math.max(0, safePatterns.length - patternLimit);

  // i18n for CTA copy
  const { t: tFeatures } = useTranslation('features');

  // Resolve teaser copy: SDUI $template > i18n fallback
  const teaserCopy = sduiConfig.teaserCopy
    ? sduiConfig.teaserCopy.replace('$count', String(gatedPatternCount))
    : tFeatures('gating.pattern_teaser.unlock_patterns', { count: gatedPatternCount });

  // Track when gated content is shown (conversion funnel) — dedup to fire once
  const hasFiredViewEvent = useRef(false);
  useEffect(() => {
    if (!hasFiredViewEvent.current && gatedPatternCount > 0 && !isPremium) {
      captureEvent('pattern_teaser_viewed', {
        content_type: 'patterns',
        gated_count: gatedPatternCount,
        total_patterns: safePatterns.length,
        tier,
        screen,
      });
      hasFiredViewEvent.current = true;
    }
  }, [gatedPatternCount, tier, screen, safePatterns.length, isPremium]);

  // Track CTA clicks
  const handleUpgrade = useCallback(() => {
    captureEvent('pattern_teaser_clicked', {
      content_type: 'patterns',
      gated_count: gatedPatternCount,
      tier,
      screen,
    });
    onUpgrade?.();
  }, [gatedPatternCount, tier, screen, onUpgrade]);

  // No patterns at all - render nothing
  if (safePatterns.length === 0) {
    return null;
  }

  // Premium users or only 1 pattern (nothing to gate) - render directly
  if (isPremium || gatedPatternCount === 0) {
    return (
      <PatternsSection
        patterns={safePatterns}
        t={t}
        testID={testID}
      />
    );
  }

  // L1 user with multiple patterns: show first visible + blur the rest
  return (
    <View testID={testID}>
      {/* First pattern visible */}
      <PatternsSection
        patterns={visiblePatterns}
        t={t}
        testID={`${testID}-visible`}
      />

      {/* Gated remaining patterns (blurred) */}
      <GatedSection
        requiredTier="drama_plus"
        contentType="patterns"
        screen={screen}
        onUpgrade={handleUpgrade}
        gatedCount={gatedPatternCount}
        blurIntensity="heavy"
        teaser={teaserCopy}
        testID={`${testID}-gated`}
      >
        {/* Preview of gated patterns (blurred) */}
        <View style={{ opacity: 0.3 }}>
          <PatternsSection
            patterns={safePatterns.slice(patternLimit)}
            t={t}
            testID={`${testID}-gated-content`}
          />
        </View>
      </GatedSection>
    </View>
  );
}
